import React, { useMemo } from 'react';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';
import { getDeployedEnvType, useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import { Layout, respGridUtil, prePageCall, getProcessEnvs } from '@marriott/mi-headless-utils';
import {
  constants,
  globalDefaultHomePage,
  globalHomePageEurope,
  IMAGE_URL_DOMAIN,
  PAGES,
} from '@marriott/mi-awp-components/constants';
import { getSubDirectoryLocale } from '@marriott/mi-awp-components';
import { PageParamsContext } from '@marriott/mi-awp-components';
import operationSignatures from '../../_constants/operationSignatures.json';
import { isEmpty } from '@marriott/shared/mi-helper-utils';

const { NEXT_PUBLIC_AEM_PATH } = process.env;
const { ENV_DEV, REDIRECT_URL } = process.env;
const isDev = process.env['NODE_ENV'] === ENV_DEV;

const mockModel = async () => {
  if (isDev) {
    // nx-ignore-next-line
    const { homePageModel } = await import('@marriott/mi-awp-components/localModel');
    return homePageModel;
  }
  return {};
};

const parseFeatureConfig = featureName => {
  const value = process.env[featureName];
  if (!value) {
    return [];
  }
  return value.split('||').map(s => s.trim());
};

const identifyRegion = countryCode => {
  // Parse regions
  const regions = parseFeatureConfig('FEATURE_HOMEPAGE_VARIATION_REGIONS');
  // Check each region for the country code
  for (const region of regions) {
    const key = `FEATURE_HOMEPAGE_VARIATION_${region.toUpperCase()}`;
    const countries = parseFeatureConfig(key);
    if (countries.includes(countryCode)) {
      return region; // Return the region if a match is found
    }
  }
  return '';
};

const identifyTranslatedVariation = (currentLocale: string) => {
  // Parse regions
  const regions = parseFeatureConfig('FEATURE_HOMEPAGE_VARIATION_REGIONS');
  // Check each region for the country code
  for (const region of regions) {
    const regionLocaleVariations = parseFeatureConfig(`FEATURE_HOMEPAGE_TRANSLATED_VARIATION_${region.toUpperCase()}`);
    for (const localeVariant of regionLocaleVariations) {
      if (localeVariant?.toLowerCase() === currentLocale?.toLowerCase()) {
        return region.toLowerCase();
      }
    }
  }
  return globalHomePageEurope;
};

const { ITEM_PATH_ROOT, ITEM_PATH_FOOTER, ITEM_PATH_HEADER } = constants;
const { HOMEPAGE } = PAGES;

// final data would have to come from 2 calls:
// 1. Page level call from AEM - that will populate the labels on components
// 2. Functional data for components like list of amenities and other related information
// import jsonContent from '../../mock/label-info.json';

export default function HomePage({ model, deployedEnvType, isAuthorMode, cookies, currentLocale }) {
  const MiApolloClient = useNextMiApolloClient(operationSignatures, deployedEnvType);

  const responsiveGridModel = respGridUtil(model?.cqItems?.root?.[':items']?.responsivegrid);
  const responsiveGridHeaderModel = respGridUtil(model?.cqItems?.root?.[':items']?.['header-responsivegrid']);
  const responsiveGridFooterModel = respGridUtil(model?.cqItems?.root?.[':items']?.['footer-responsivegrid']);

  const {
    metaNames,
    metaProperties,
    canonical,
    title,
    staticComponentClientLibsJS,
    staticComponentClientLibsCSS,
    gridCss,
    bgColor,
    hrefLangList,
  } = model;

  const contextValue = useMemo(
    () => ({
      userId: cookies.UserIdToken,
      currentPage: HOMEPAGE,
      backgroundColor: bgColor,
      currentLocale,
    }),
    [bgColor, cookies.UserIdToken, currentLocale]
  );

  //  Updating the meta properties and title as per SEO requirements
  const metaProperty = metaProperties;

  const updateMetaProperties = () => {
    const urlKey = metaNames?.find(item => item?.name === 'url');
    const titleKey = metaNames?.find(item => item?.name === 'title');

    metaProperty?.map(item => {
      if (item?.name === 'og:url' && !item?.value) item.value = urlKey?.value;
    });

    metaProperty?.map(item => {
      if (item?.name === 'og:title' && !item?.value) item.value = titleKey?.value;
    });

    metaProperty?.map(item => {
      if (item?.name === 'og:image') {
        if (!item.value.includes(IMAGE_URL_DOMAIN)) {
          const imgUrl = IMAGE_URL_DOMAIN + item.value;
          item.value = imgUrl;
        }
      }
    });
  };

  updateMetaProperties();
  return (
    <ApolloProvider client={MiApolloClient}>
      <PageParamsContext.Provider value={contextValue}>
        <Layout
          metaNames={metaNames}
          metaProps={metaProperty}
          title={title}
          staticComponentClientLibsCSS={staticComponentClientLibsCSS}
          staticComponentClientLibsJS={staticComponentClientLibsJS}
          isAuthorMode={isAuthorMode}
          gridCss={gridCss}
          canonical={canonical}
          hreflangList={hrefLangList}
        >
          <section>
            {responsiveGridHeaderModel && (
              <ResponsiveGrid
                {...responsiveGridHeaderModel}
                model={responsiveGridHeaderModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_HEADER}
              />
            )}

            <ResponsiveGrid
              {...responsiveGridModel}
              model={responsiveGridModel}
              pagePath={NEXT_PUBLIC_AEM_PATH}
              itemPath={ITEM_PATH_ROOT}
            />

            {responsiveGridFooterModel && (
              <ResponsiveGrid
                {...responsiveGridFooterModel}
                model={responsiveGridFooterModel}
                pagePath={NEXT_PUBLIC_AEM_PATH}
                itemPath={ITEM_PATH_FOOTER}
              />
            )}
          </section>
        </Layout>
      </PageParamsContext.Provider>
    </ApolloProvider>
  );
}

const removeQueryParam = (url: string) => {
  return url.includes('?') ? url.split('?')[0] : url;
};

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const awpSpecificData = async (context: { [key: string]: any }) => {
  //any page specific code can be written here.
  const deployedEnvType = getDeployedEnvType();

  const { GOOGLE_MAP_API_KEY } = getProcessEnvs();
  const resolvedUrl = context?.resolvedUrl;
  const currentLocale = context?.req?.['headers']?.['accept-language'];
  const countryInfo = context?.req?.headers['x-akamai-edgescape']?.match(/country_code=([A-Za-z]{2})/);
  const countryCode = countryInfo?.length && countryInfo[1];
  const region = countryCode ? identifyRegion(countryCode).toLowerCase() : '';
  const resolvedUrlWithoutParams = removeQueryParam(resolvedUrl);
  const localeTranslatedRegion = identifyTranslatedVariation(currentLocale);
  let uri = '';

  if (currentLocale === 'en-US') {
    if (!!countryCode && !!region) uri = resolvedUrlWithoutParams + `/home/${region}`;
    else uri = resolvedUrlWithoutParams;
  } else if (localeTranslatedRegion === globalDefaultHomePage) {
    uri = resolvedUrlWithoutParams;
  } else {
    uri = resolvedUrlWithoutParams + `/home/${localeTranslatedRegion}`;
  }

  return {
    operationSignatures: operationSignatures,
    deployedEnvType,
    uri: uri,
    GOOGLE_MAP_API_KEY,
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps = async context => {
  const response = prePageCall(awpSpecificData, mockModel, false, true, true);
  const componentProps = await response(context).then(data => data);
  const currentLocale = context?.req?.['headers']?.['accept-language'] ?? 'en-US';
  const subLocale = getSubDirectoryLocale(currentLocale).replace('/', '');

  if (
    componentProps?.props?.model &&
    !isEmpty(componentProps?.props?.model?.cqPath) &&
    !isEmpty(componentProps?.props?.model?.cqItems) &&
    !isEmpty(componentProps?.props?.model?.cqItems?.root[':items']?.responsivegrid[':items'])
  ) {
    return {
      props: {
        deployedEnvType: componentProps?.props?.apolloEnvVars?.DEPLOYED_ENV_TYPE,
        ...componentProps.props,
      },
    };
  } else {
    return {
      redirect: {
        destination: `/${subLocale}${REDIRECT_URL}`,
        permanent: false,
      },
    };
  }
};
